import React, { useEffect, useState } from "react";
import { FiAward, FiCheck, FiShoppingBag, FiTrendingUp } from "react-icons/fi";
import { api } from "../../config/api";
import showToast from "../../utils/toastNotifications";
import { currencyFormatter } from "../../utils/formatting";

const Progress = ({ label, value, target, formatValue = (amount) => amount }) => {
  const percent = target > 0 ? Math.min(100, Math.round((Number(value || 0) / target) * 100)) : 100;
  return (
    <div>
      <div className="mb-1.5 flex justify-between gap-3 text-sm">
        <span className="font-semibold text-primary">{label}</span>
        <span className="text-gray">{formatValue(value)} of {formatValue(target)}</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
};

const LoyaltyRewards = () => {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/loyalty/me")
      .then((response) => setProgress(response.data.data))
      .catch((error) => showToast("error", error.message || "Unable to fetch loyalty progress"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="px-4 pb-6 pt-28 font-inter text-gray xl:pt-6">Loading loyalty rewards...</div>;
  }

  const current = progress?.currentLevel || { level: 0, name: "Standard", discountPercent: 30 };
  const next = progress?.nextLevel;

  if (!progress?.enabled) {
    return <div className="px-4 pb-6 pt-28 font-inter text-gray xl:pt-6">The loyalty program is not currently available.</div>;
  }

  return (
    <div className="mx-auto w-full max-w-6xl px-4 pb-16 pt-28 sm:px-6 sm:pb-20 xl:px-8 xl:pb-24 xl:pt-6">
      <header className="mb-6 sm:mb-8">
        <span className="inline-flex items-center gap-2 rounded-full bg-soft-blue px-3 py-1 font-inter text-xs font-semibold text-primary">
          <FiAward aria-hidden="true" /> Your loyalty journey
        </span>
        <p className="mt-2 max-w-2xl font-inter text-sm leading-6 text-gray">
          Complete orders or reach the spending target to unlock a higher discount.
        </p>
      </header>

      <section className="grid overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm lg:grid-cols-[0.8fr_1.2fr]">
        <div className="flex flex-col justify-between bg-soft-blue p-5 sm:p-7 lg:p-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-inter text-xs font-bold uppercase tracking-wider text-gray">Your current reward</p>
              <p className="mt-2 font-inter text-sm font-semibold text-gray">{current.name}</p>
              <p className="mt-1 font-roboto text-4xl font-bold text-primary sm:text-5xl">{current.discountPercent}% off</p>
            </div>
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white text-primary shadow-sm sm:h-16 sm:w-16">
              <FiAward className="h-7 w-7 sm:h-8 sm:w-8" aria-hidden="true" />
            </div>
          </div>
          <p className="mt-5 font-inter text-sm leading-6 text-gray">Applied automatically to eligible future orders.</p>
        </div>

        <div className="p-5 sm:p-7 lg:p-8">
          {next ? (
            <>
              <h2 className="font-roboto text-lg font-bold text-primary sm:text-xl">Unlock {next.discountPercent}% off</h2>
              <p className="mt-1 font-inter text-sm text-gray">Reach either target—you do not need both.</p>
              <div className="mt-5 grid items-center gap-4 sm:grid-cols-[1fr_auto_1fr]">
                <Progress label="Completed orders" value={progress.qualifyingOrders || 0} target={next.orders} />
                <div className="text-center font-inter text-xs font-bold uppercase tracking-widest text-gray">OR</div>
                <Progress
                  label="Qualifying spending"
                  value={progress.qualifyingSpend || 0}
                  target={next.spend}
                  formatValue={currencyFormatter.format}
                />
              </div>
              <p className="mt-5 rounded-lg bg-slate-50 p-3 font-inter text-sm leading-6 text-gray">
                Complete <strong>{progress.remainingOrders}</strong> more qualifying order{progress.remainingOrders === 1 ? "" : "s"} or add <strong>{currencyFormatter.format(progress.remainingSpend || 0)}</strong> in qualifying spending.
              </p>
            </>
          ) : (
            <div className="flex h-full min-h-[150px] flex-col items-center justify-center text-center">
              <FiAward className="h-10 w-10 text-primary" aria-hidden="true" />
              <h2 className="mt-3 font-roboto text-xl font-bold text-primary">Highest level reached</h2>
              <p className="mt-1 font-inter text-sm text-gray">You have reached the highest loyalty level.</p>
            </div>
          )}
        </div>
      </section>

      <section className="mt-8 sm:mt-10" aria-labelledby="loyalty-levels-heading">
        <div className="mb-5">
          <h2 id="loyalty-levels-heading" className="font-roboto text-xl font-bold text-primary sm:text-2xl">Your rewards journey</h2>
          <p className="mt-1 font-inter text-sm text-gray">See where you are now and what comes next.</p>
        </div>

        <ol className="relative space-y-0 xl:grid xl:grid-cols-6 xl:gap-3 xl:space-y-0">
          {(progress?.levels || []).map((level, index, levels) => {
            const isCurrent = level.level === current.level;
            const isCompleted = level.level < current.level;
            const isLast = index === levels.length - 1;
            return (
              <li key={level.level} className="relative grid grid-cols-[40px_1fr] gap-3 pb-5 last:pb-0 xl:flex xl:flex-col xl:gap-0 xl:pb-0">
                {!isLast && (
                  <span className={`absolute left-[19px] top-10 h-[calc(100%-40px)] w-0.5 ${isCompleted ? "bg-primary" : "bg-slate-200"} xl:left-[calc(50%+20px)] xl:top-5 xl:h-0.5 xl:w-[calc(100%-28px)]`} aria-hidden="true" />
                )}
                <div className={`relative z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 font-inter text-sm font-bold xl:mx-auto ${isCurrent ? "border-primary bg-primary text-white ring-4 ring-soft-blue" : isCompleted ? "border-primary bg-primary text-white" : "border-slate-200 bg-white text-gray"}`}>
                  {isCompleted ? <FiCheck className="h-5 w-5" aria-label="Completed" /> : level.level + 1}
                </div>
                <div className={`rounded-xl border p-4 xl:mt-4 xl:min-h-[184px] xl:flex-1 ${isCurrent ? "border-primary bg-soft-blue shadow-sm" : isCompleted ? "border-primary/30 bg-white" : "border-slate-200 bg-white"}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2 xl:block">
                    <div>
                      <p className="font-inter text-sm font-semibold text-gray">{level.name}</p>
                      <p className="mt-0.5 font-roboto text-xl font-bold text-primary">{level.discountPercent}% off</p>
                    </div>
                    {isCurrent && <span className="rounded-full bg-primary px-2.5 py-1 font-inter text-[11px] font-semibold text-white xl:mt-2 xl:inline-block">You are here</span>}
                    {isCompleted && <span className="rounded-full bg-soft-blue px-2.5 py-1 font-inter text-[11px] font-semibold text-primary xl:mt-2 xl:inline-block">Completed</span>}
                  </div>
                  {level.level > 0 && (
                    <div className="mt-3 space-y-1.5 border-t border-slate-200 pt-3 font-inter text-sm text-gray">
                      <p className="flex items-center gap-2"><FiShoppingBag className="shrink-0" aria-hidden="true" /> {level.orders} orders</p>
                      <p className="flex items-center gap-2"><FiTrendingUp className="shrink-0" aria-hidden="true" /> {currencyFormatter.format(level.spend)} spending</p>
                      <p className="text-xs font-semibold uppercase tracking-wide">Reach either target</p>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </section>

      <footer className="mt-8 border-t border-slate-200 pt-5 sm:mt-10">
        <p className="font-inter text-xs leading-5 text-gray">
          Only eligible orders completed after the program launch count. Completion may be confirmed by you, by our team, or automatically after delivery. After six months without a completed qualifying order, your level reduces by one step and continues reducing every three months until the standard 30% level. A completed qualifying order resets the inactivity clock.
        </p>
        <a href="/terms-and-conditions#loyalty-program-terms" target="_blank" rel="noopener noreferrer" className="mt-3 inline-block font-inter text-xs font-semibold text-primary underline">
          Loyalty Program Terms
        </a>
      </footer>
    </div>
  );
};

export default LoyaltyRewards;
